import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { SubTitle, Title } from '../../../styles/common';
import { postData } from '../../../utils/postData';

const UsersIdConfirmation = ({
    onClose,
    data,
    mode = 'new',
    ...otherProps
}) => {
    const { t } = useTranslation();
    const ERROR_MSG_KEY = 'errorMsgs$serverDefault';

    const { confirmationEmail } = data;
    const emailList = confirmationEmail && typeof confirmationEmail === 'string'
        ? confirmationEmail.split(',').map(email => email.trim()).filter(email => email)
        : [];

    const defaultOption = emailList.length > 0 ? '0' : 'custom';
    const [selectedOption, setSelectedOption] = useState(defaultOption);
    const [customEmail, setCustomEmail] = useState('');
    const [isCustomEmailValid, setIsCustomEmailValid] = useState(false);
    const [notificationResponse, setNotificationResponse] = useState({ show: false, message: '', color: '' });
    const [isLoading, setIsLoading] = useState(false);

    const handleOptionChange = (value) => {
        setSelectedOption(value);
    };

    const emailOptionLabel = (index) => {
        if (index === 0) return t('idv$resend$primaryEmail');
        if (index === 1) return t('idv$resend$secondaryEmail');
        return `Email ${index + 1}`;
    };

    const handleCustomEmail = (event) => {
        const checkIsValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        setCustomEmail(event?.target?.value);
        setIsCustomEmailValid(checkIsValidEmail.test(event?.target?.value));
    };

    const getEffectiveEmail = () => {
        if (selectedOption === 'custom') return customEmail;
        return emailList[parseInt(selectedOption)];
    };

    const submitUserForm = async () => {
        setIsLoading(true);
        const endpoint = `/user/${data.username}/verify`;
        const body = mode === 'resend'
            ? { verificationType: 'EMAIL', email: getEffectiveEmail(), isResend: true }
            : { verificationType: 'EMAIL', email: getEffectiveEmail() };

        const res = await postData({ endpoint, body });

        if (res?.status === 200) {
            setIsLoading(false);
            const json = await res?.json();
            if (json) {
                setNotificationResponse({ show: true, message: json?.message, color: 'primary' });
                setTimeout(() => {
                    onClose({ ...data, modal: false, submit: true });
                }, 2000);
            }
        } else {
            setIsLoading(false);
            const json = await res?.json();
            if (json) {
                let errorMessage = json?.message || t(ERROR_MSG_KEY);
                const errorCode = json?.code || '';
                if (errorCode.includes('USER_DISABLED')) {
                    errorMessage = t('idv$error$userDisabled') || 'Cannot verify: User account is disabled';
                } else if (errorCode.includes('USER_DELETED')) {
                    errorMessage = t('idv$error$userDeleted') || 'Cannot verify: User account is deleted';
                } else if (errorCode.includes('activeVerificationLinkExpired')) {
                    errorMessage = t('idv$resend$expired')
                        || 'The verification link has expired. Please close this and start a new verification.';
                } else if (errorMessage.includes(':')) {
                    errorMessage = errorMessage.split(/:(.+)/)?.[1]?.trim() || errorMessage;
                }
                setNotificationResponse({ show: true, message: errorMessage, color: 'danger' });
            } else {
                setNotificationResponse({ show: true, message: t(ERROR_MSG_KEY), color: 'danger' });
            }
        }
    };

    const closeModal = () => {
        onClose({ modal: false, submit: false });
    };

    const isSubmitDisabled = () => {
        if (isLoading) return true;
        if (selectedOption === 'custom') return !isCustomEmailValid;
        if (selectedOption !== 'custom' && emailList.length === 0) return true;
        return false;
    };

    return (
        <Modal isOpen="false" size="lg" centered="true">
            <ModalBody>
                {notificationResponse?.show && (
                    <Alert color={notificationResponse?.color}>
                        {notificationResponse?.message ? notificationResponse?.message : t(ERROR_MSG_KEY)}
                    </Alert>
                )}
                <Title style={{ margin: '0.75rem' }}>
                    {t(mode === 'resend' ? 'idv$resend$modalTitle' : 'usersTable$column$actions$modal$title')}
                </Title>
                <SubTitle style={{ margin: ' 0 0.75rem' }}>
                    {t(mode === 'resend' ? 'idv$resend$modalSubTitle' : 'usersTable$column$actions$modal$subTitle')}
                </SubTitle>

                <Form style={{ paddingLeft: '30px' }}>
                    {emailList.length === 0 ? (
                        <FormGroup>
                            <Input
                                name="emailOption"
                                type="radio"
                                value="0"
                                disabled
                            />
                            <Label>{t('usersTable$column$actions$modal$useMailAddress')} (No Email Found)</Label>
                        </FormGroup>
                    ) : (
                        emailList.map((email, index) => (
                            <FormGroup key={email}>
                                <Input
                                    name="emailOption"
                                    type="radio"
                                    value={String(index)}
                                    onChange={() => handleOptionChange(String(index))}
                                    checked={selectedOption === String(index)}
                                />
                                <Label>
                                    {emailOptionLabel(index)}: {email}
                                </Label>
                            </FormGroup>
                        ))
                    )}
                    <FormGroup>
                        <Input
                            name="emailOption"
                            type="radio"
                            value="custom"
                            onChange={() => handleOptionChange('custom')}
                            checked={selectedOption === 'custom'}
                        />
                        <Label>
                            {mode === 'resend'
                                ? t('idv$resend$manualEmail')
                                : t('usersTable$column$actions$modal$useDifferentMailAddress')}
                        </Label>
                    </FormGroup>
                    {selectedOption === 'custom' && (
                        <FormGroup>
                            <Input
                                id="user-email"
                                name="email"
                                placeholder="Enter email address"
                                type="email"
                                style={{ border: 'none', borderBottom: '1px solid #ced4da' }}
                                value={customEmail}
                                onChange={(e) => handleCustomEmail(e)}
                            />
                        </FormGroup>
                    )}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="primary"
                    outline
                    onClick={submitUserForm}
                    disabled={isSubmitDisabled()}
                >
                    {t('modalLabels$sendEmail')}
                </Button>
                <Button
                    color="primary"
                    outline
                    onClick={closeModal}
                    disabled={isLoading}
                >
                    {t('modalLabels$close')}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default UsersIdConfirmation;
